import { createAsyncThunk, createSlice, PayloadAction, SerializedError } from "@reduxjs/toolkit";
import { RootState } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import _ from "lodash";
import MessagingAPIService, { MessageQuery } from "messaging/_api/messaging-api-service";
import Message from "messaging/message";
import Session from "users/__session/session";
import Comment from "work/proposals/comments/comment";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import Proposal from "work/proposals/proposal";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import ProposalReviewer from "work/proposals/reviewers/proposal-reviewer";

type CommentStoreState = {
  entries: Record<string, Comment[]>;
  loading: Record<string, boolean>;
  error: Record<string, SerializedError | null>;
};

const initialState: CommentStoreState = {
  entries: {},
  loading: {},
  error: {},
};

export const populateThreadComments = createAsyncThunk(
  "comments/getCommentsByThread",
  async (
    {
      session,
      thread,
      query,
    }: {
      session: Session;
      thread: CommentThread;
      query: MessageQuery;
    },
    thunkAPI,
  ) => {
    try {
      const apiService = new MessagingAPIService(session);
      const commentMessages = await apiService.getMessagesByForum(
        thread.toForum({
          isViewingAsVendor: session.context?.viewingAsVendor ?? false,
        }),
        query,
      );
      return commentMessages.data.map((message: Message) => Comment.fromMessage(message));
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const saveComments = createAsyncThunk(
  "comments/saveComments",
  async (
    {
      session,
    }: {
      session: Session;
    },
    thunkAPI,
  ) => {
    try {
      const state = thunkAPI.getState() as RootState;

      const unsavedComments: Comment[] = Object.values(state.comments.entries).flatMap((comments) =>
        comments.filter(
          (comment) =>
            !comment.published &&
            !comment.isAutoGenerated &&
            (comment.markedForPublish ||
              comment.markedForCreation ||
              comment.markedForEdit ||
              comment.markedForReadReceipt ||
              comment.markedForUnread),
        ),
      );
      if (!unsavedComments.length) {
        return [];
      }

      const apiService = new MessagingAPIService(session);
      const messagesToSave = unsavedComments.map((comment) =>
        comment.toMessage({
          isViewingAsVendor: session.context?.viewingAsVendor ?? false,
        }),
      );
      const messages = await apiService.createBulkMessages(messagesToSave);
      return messages.map(Comment.fromMessage);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const publishComments = createAsyncThunk(
  "comments/publishComments",
  async (
    {
      session,
    }: {
      session: Session;
    },
    thunkAPI,
  ) => {
    try {
      const state = thunkAPI.getState() as RootState;

      const pendingComments: Comment[] = Object.values(state.comments.entries)
        .flatMap((comments) => comments.filter((comment) => !comment.published))
        .map((comment) => comment.markForPublish());

      const apiService = new MessagingAPIService(session);
      const messagesToSave = pendingComments.map((comment) =>
        comment.toMessage({
          isViewingAsVendor: session.context?.viewingAsVendor ?? false,
        }),
      );
      const messages = await apiService.createBulkMessages(messagesToSave);
      return messages.map(Comment.fromMessage);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

const commentsSlice = createSlice({
  name: "comments",
  initialState,
  reducers: {
    addComment: (state, action: PayloadAction<Comment>) => {
      const fieldKey = action.payload.thread.field?.cacheKey;
      const proposalId = action.payload.thread.proposalId;
      if (!fieldKey || !proposalId) {
        return;
      }
      const commentFieldKey = `${proposalId.value}-${fieldKey}`;

      const existingThreadComments =
        state.entries[commentFieldKey]?.filter((existingComment) => !existingComment.isEqualTo(action.payload)) ?? [];

      existingThreadComments.push(action.payload);
      state.entries[commentFieldKey] = existingThreadComments;
    },
    removeCommentById: (state, action: PayloadAction<Guid>) => {
      for (const fieldCommentsKey of Object.keys(state.entries)) {
        state.entries[fieldCommentsKey] = state.entries[fieldCommentsKey]?.filter(
          (comment) => !comment.id?.isEqualTo(action.payload),
        );
      }
    },
    removeComment: (state, action: PayloadAction<Comment>) => {
      const fieldKey = action.payload.thread.field?.cacheKey;
      const proposalId = action.payload.thread.proposalId;
      if (!fieldKey || !proposalId) {
        return;
      }
      const commentFieldKey = `${proposalId.value}-${fieldKey}`;

      state.entries[commentFieldKey] = state.entries[commentFieldKey]?.filter(
        (comment) => !comment.isEqualTo(action.payload),
      );
    },

    clearUnsavedCommentsByProposalId(state, action: PayloadAction<Guid>) {
      for (const fieldCommentsKey of Object.keys(state.entries)) {
        state.entries[fieldCommentsKey] = state.entries[fieldCommentsKey]?.filter(
          (comment) => !(comment.thread.proposalId.isEqualTo(action.payload) && !comment.created),
        );
      }
    },
    updateAutoGeneratedComments: (state, action: PayloadAction<Comment[]>) => {
      const autoGeneratedComments = action.payload;
      const existingCommentsFieldKeys: string[] = _.uniq(
        autoGeneratedComments.map((comment) => `${comment.thread.proposalId.value}-${comment.thread.field?.cacheKey}`),
      );

      for (const commentsFieldKey of Object.keys(state.entries)) {
        const currentComments = state.entries[commentsFieldKey] ?? [];
        state.entries[commentsFieldKey] = currentComments.filter(
          (comment) => !(comment.isAutoGenerated && !comment.created),
        );
      }

      for (const commentsFieldKey of existingCommentsFieldKeys) {
        const newComments = state.entries[commentsFieldKey] ?? [];
        const threadAutoComments = autoGeneratedComments.filter(
          (comment) => `${comment.thread.proposalId.value}-${comment.thread.field?.cacheKey}` === commentsFieldKey,
        );
        for (const comment of threadAutoComments) {
          newComments.push(comment);
        }
        state.entries[commentsFieldKey] = newComments;
      }
    },
    updateThreadSubscribers: (state, action: PayloadAction<{ proposal: Proposal; session: Session }>) => {
      const { proposal, session } = action.payload;

      const userIsAssociatedWithClient = proposal.userIsAssociatedWithClient(session.user);
      const userIsAssociatedWithVendor = proposal.userIsAssociatedWithVendor(session.user);

      let currentReviewers: ProposalReviewer[] = [];
      if (userIsAssociatedWithClient) {
        currentReviewers = proposal.clientReviewers;
      } else if (userIsAssociatedWithVendor) {
        currentReviewers = proposal.vendorReviewers;
      } else {
        console.warn("User is not associated with the proposal as a reviewer or negotiator");
        return;
      }

      let proposalComments: Comment[] = [];
      for (const commentsFieldKey of Object.keys(state.entries)) {
        const currentComments =
          state.entries[commentsFieldKey]
            .filter((comment) => comment.thread.proposalId.isEqualTo(proposal.id) && comment.isPending)
            .map((c) => c as Comment) ?? [];
        proposalComments = proposalComments.concat(currentComments);
      }

      proposalComments.forEach((comment) => {
        const fieldKey = `${comment.thread.proposalId.value}-${comment.thread.field?.cacheKey}`;
        comment.thread.subscribers = currentReviewers.map((reviewer) => reviewer.entityMember);
        const newComments = state.entries[fieldKey]?.filter((c) => !c.id?.isEqualTo(comment.id)) ?? [];
        newComments.push(comment);
        state.entries[fieldKey] = newComments;
      });
    },
  },
  extraReducers: (builder) => {
    builder.addCase(populateThreadComments.pending, (state, action) => {
      const fieldKey = action.meta.arg.thread.field.cacheKey;
      const proposalId = action.meta.arg.thread.proposalId;
      if (!fieldKey || !proposalId) {
        return;
      }
      const commentFieldKey = `${proposalId.value}-${fieldKey}`;

      state.loading[commentFieldKey] = true;
      state.error[commentFieldKey] = null;
    });
    builder.addCase(populateThreadComments.fulfilled, (state, action) => {
      const fieldKey = action.meta.arg.thread.field.cacheKey;
      const proposalId = action.meta.arg.thread.proposalId;
      if (!fieldKey || !proposalId) {
        return;
      }
      const commentFieldKey = `${proposalId.value}-${fieldKey}`;

      state.loading[commentFieldKey] = false;
      let existingThreadComments = state.entries[commentFieldKey] || [];

      for (const comment of action.payload) {
        if (existingThreadComments.some((existingComment) => existingComment.id?.isEqualTo(comment.id))) {
          existingThreadComments = existingThreadComments.filter(
            (existingComment) => !existingComment.id?.isEqualTo(comment.id),
          );
        }
        existingThreadComments.push(comment);
        state.entries[commentFieldKey] = existingThreadComments;
      }
    });
    builder.addCase(populateThreadComments.rejected, (state, action) => {
      const fieldKey = action.meta.arg.thread.field.cacheKey;
      const proposalId = action.meta.arg.thread.proposalId;
      if (!fieldKey || !proposalId) {
        return;
      }
      const commentFieldKey = `${proposalId.value}-${fieldKey}`;

      state.loading[commentFieldKey] = false;
      state.error[commentFieldKey] = action.error;
    });
    builder.addCase(saveComments.pending, (state) => {
      for (const [commentFieldKey, comments] of Object.entries(state.entries)) {
        if (comments.some((comment) => comment.isPending && !comment.isAutoGenerated)) {
          state.loading[commentFieldKey] = true;
          state.error[commentFieldKey] = null;
        }
      }
    });
    builder.addCase(saveComments.fulfilled, (state, action) => {
      for (const comment of action.payload) {
        const fieldKey = comment.thread.field.cacheKey;
        const proposalId = comment.thread.proposalId;
        if (!fieldKey || !proposalId) {
          continue;
        }
        const commentFieldKey = `${proposalId.value}-${fieldKey}`;

        let existingThreadComments = state.entries[`${proposalId.value}-${fieldKey}`] || [];
        existingThreadComments = existingThreadComments.filter(
          (existingComment) => !existingComment.id?.isEqualTo(comment.id),
        );
        existingThreadComments.push(comment);
        state.entries[commentFieldKey] = existingThreadComments;
        state.loading[commentFieldKey] = false;
        state.error[commentFieldKey] = null;
      }
    });
    builder.addCase(saveComments.rejected, (state, action) => {
      for (const [commentFieldKey, comments] of Object.entries(state.entries)) {
        if (comments.some((comment) => comment.isPending)) {
          state.loading[commentFieldKey] = false;
          state.error[commentFieldKey] = action.error;
        }
      }
    });
  },
});

export const {
  addComment,
  removeCommentById,
  removeComment,
  clearUnsavedCommentsByProposalId,
  updateAutoGeneratedComments,
  updateThreadSubscribers,
} = commentsSlice.actions;

export const getCommentsByField =
  (proposalId?: Guid | null, field?: ProposalField) =>
  (state: RootState): Comment[] | undefined => {
    if (!proposalId || !field?.cacheKey) return [];
    return state.comments.entries[`${proposalId.value}-${field?.cacheKey}`] ?? undefined;
  };
export const getIsLoadingComments = (proposalId?: Guid | null, field?: ProposalField) => (state: RootState) => {
  if (!proposalId || !field?.cacheKey) return false;
  return state.comments.loading[`${proposalId.value}-${field?.cacheKey}`] ?? false;
};
export const getArePendingComments = (proposal?: Proposal | null) => (state: RootState) => {
  if (!proposal?.id?.value) return false;
  return Object.values(state.comments?.entries)
    .flat()
    .some((comment) => comment.thread.proposalId.isEqualTo(proposal.id) && comment.isPending);
};
export const getAreUnsavedComments = (proposal?: Proposal | null) => (state: RootState) => {
  if (!proposal?.id?.value) return false;
  return Object.values(state.comments?.entries)
    .flat()
    .some(
      (comment) => comment.thread.proposalId.isEqualTo(proposal.id) && !comment.isAutoGenerated && comment.isPending,
    );
};

export default commentsSlice;
